import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { listClients, clientLabel } from '../../utils/clients';
import { createQuote, formatMoney, formatMonthly, lineItemsTotal } from '../../utils/quotes';
import { CARE_TIER_LABELS, carePriceFor, scheduleFor, splitTotal, applyPercent } from '../../constants/pricing';
import { QUOTE_VALIDITY_DAYS } from '../../constants/business';
import DescriptionItemsInput from './DescriptionItemsInput';
import { descriptionBullets } from '../../utils/trackingLinks';
import { MONO } from '../../constants/fonts';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  sectionLabel: ds.sectionLabel,
  input: ds.input,
  select: ds.select,
  textarea: ds.textarea,
  hint: ds.hint,
  inputError: ds.inputError,
  fieldError: ds.fieldError,
  disabled: ds.disabled,
  // Narrow, because it sits beside a description in a line-item row.
  priceInputWrap: { ...ds.priceInputWrap, width: 120, flexShrink: 0 },
  // Stands on its own under the tier select, so it carries its own bottom
  // margin — without it, the hint's negative top margin rides over the field.
  carePriceWrap: { ...ds.priceInputWrap, width: 160, marginBottom: 12 },
  validityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: 140,
    marginBottom: 12,
  },
  validityInput: {
    width: '100%',
    minWidth: 0,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  priceSymbol: ds.priceSymbol,
  priceInput: ds.priceInput,
  addButton: ds.dashedButton,
  removeButton: ds.iconButton,
  submit: ds.primaryButton,
  totalRow: ds.totalRow,
  totalValue: ds.totalValue,
  listMeta: ds.listMeta,
  success: ds.success,
  error: ds.error,
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  categoryRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  categoryButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    background: '#fff',
    color: '#555',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  // Full shorthand, matching categoryButton above: swapping a longhand in and
  // out would leave the deselected button with no border colour.
  categoryButtonActive: {
    background: '#111',
    color: '#fff',
    border: '1px solid #111',
  },
  // The % sits beside the field, not inside it: number inputs put their
  // spinners hard against the right edge, and any absolute position that
  // clears them in one browser overlaps them in another.
  percentWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: 96,
    flexShrink: 0,
  },
  percentSymbol: {
    color: '#888',
    fontSize: '0.9rem',
    flexShrink: 0,
  },
  // No left padding for a currency symbol, unlike priceInput.
  percentInput: {
    width: '100%',
    minWidth: 0,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  stageAmount: {
    width: 88,
    flexShrink: 0,
    textAlign: 'right',
    fontFamily: MONO,
    fontSize: '0.85rem',
    color: '#555',
  },
  itemBlock: {
    marginBottom: 12,
  },
  // Indented under its priced line, and given the same bar-and-panel treatment
  // as the client notes — as plain text it read as a caption rather than a
  // control next to all the bordered inputs.
  featuresWrap: {
    marginLeft: 16,
  },
  featuresToggle: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #ececE6',
    background: '#f9f9f7',
    color: '#555',
    fontSize: '0.82rem',
    cursor: 'pointer',
    textAlign: 'left',
  },
  featuresToggleOpen: {
    borderRadius: '8px 8px 0 0',
  },
  featuresLabel: {
    fontWeight: 600,
    flexShrink: 0,
  },
  featurePreview: {
    flex: 1,
    minWidth: 0,
    color: '#999',
    fontSize: '0.8rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  featuresChevron: {
    color: '#aaa',
    flexShrink: 0,
  },
  featuresBody: {
    padding: 12,
    border: '1px solid #ececE6',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    background: '#fff',
  },
  subtotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    fontSize: '0.85rem',
    color: '#777',
  },
  discountRow: {
    color: '#1a7f37',
    fontWeight: 600,
  },
  strike: {
    textDecoration: 'line-through',
    color: '#aaa',
    marginRight: 8,
    fontWeight: 400,
  },
  itemDescription: {
    flex: 1,
    minWidth: 0,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
};


const emptyItem = () => ({ description: '', amount: '', features: '' });

// Same approach as ContactScreen's enquiry form: mirror the in-progress form to
// localStorage so a refresh or a closed tab doesn't lose it. Every access is
// wrapped because storage throws in private mode and when cookies are blocked.
const STORAGE_KEY = 'ddhapps-quote-draft';

// localStorage is untrusted input — it can be hand-edited, and a draft saved
// by an older version of this form may not match today's shape. Every field is
// coerced to what the component expects so a bad draft can't crash the panel.
function loadDraft() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    const items = Array.isArray(raw.lineItems) ? raw.lineItems : [];
    return {
      clientEmail: typeof raw.clientEmail === 'string' ? raw.clientEmail : '',
      title: typeof raw.title === 'string' ? raw.title : '',
      notes: typeof raw.notes === 'string' ? raw.notes : '',
      contractUrl: typeof raw.contractUrl === 'string' ? raw.contractUrl : '',
      contractText: typeof raw.contractText === 'string' ? raw.contractText : '',
      lineItems: items.map((item) => ({
        description: typeof item?.description === 'string' ? item.description : '',
        amount: typeof item?.amount === 'string' || typeof item?.amount === 'number' ? String(item.amount) : '',
        features: typeof item?.features === 'string' ? item.features : '',
      })),
    };
  } catch {
    return {};
  }
}

function saveDraft(draft) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {}
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export default function QuotesPanel() {
  const { user } = useAuth();
  // Read once, on mount. Keeping panels mounted covers tab switches, but a
  // refresh or a closed tab would still lose a long pasted contract.
  const [draft] = useState(loadDraft);
  const [clients, setClients] = useState([]);
  const [clientsError, setClientsError] = useState('');
  const [clientEmail, setClientEmail] = useState(draft.clientEmail || '');
  const [title, setTitle] = useState(draft.title || '');
  const [category, setCategory] = useState(draft.category || 'website');
  // Percentages only — the pound amounts are derived from the total, so they
  // can't fall out of step with it while the breakdown is still being edited.
  const [schedule, setSchedule] = useState(draft.schedule?.length ? draft.schedule : scheduleFor('website'));
  const [careTier, setCareTier] = useState(draft.careTier || 'none');
  const [careAmount, setCareAmount] = useState(draft.careAmount || '');
  const [validityDays, setValidityDays] = useState(draft.validityDays || String(QUOTE_VALIDITY_DAYS));
  const [discountPercent, setDiscountPercent] = useState(draft.discountPercent || '');
  const [careDiscountMonths, setCareDiscountMonths] = useState(draft.careDiscountMonths || '');
  const [lineItems, setLineItems] = useState(draft.lineItems?.length ? draft.lineItems : [emptyItem()]);
  const [notes, setNotes] = useState(draft.notes || '');
  const [contractUrl, setContractUrl] = useState(draft.contractUrl || '');
  const [contractText, setContractText] = useState(draft.contractText || '');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [openFeatures, setOpenFeatures] = useState({});

  useEffect(() => {
    saveDraft({ clientEmail, title, category, lineItems, notes, contractUrl, contractText, schedule, careTier, careAmount, validityDays, discountPercent, careDiscountMonths });
  }, [clientEmail, title, category, lineItems, notes, contractUrl, contractText, schedule, careTier, careAmount, validityDays, discountPercent, careDiscountMonths]);

  // Switching category resets both to that category's published terms. Any
  // bespoke figures are deliberately discarded: silently keeping app
  // percentages on a website quote is the kind of thing you'd never spot.
  const changeCategory = (next) => {
    setCategory(next);
    setSchedule(scheduleFor(next));
    setCareAmount(careTier === 'none' ? '' : String(carePriceFor(next, careTier) ?? ''));
  };

  const changeCareTier = (tier) => {
    setCareTier(tier);
    setCareAmount(tier === 'none' ? '' : String(carePriceFor(category, tier) ?? ''));
  };

  const changeStagePercent = (index, value) => {
    setSchedule((prev) => prev.map((stage, i) => (
      i === index ? { ...stage, percent: Number(value) || 0 } : stage
    )));
  };

  // Clearing on change is what makes the red border feel responsive rather
  // than sticky — same behaviour as ContactScreen's `set` helper.
  const clearError = (key) => setErrors((prev) => (prev[key] ? { ...prev, [key]: null } : prev));

  useEffect(() => {
    listClients()
      .then(setClients)
      .catch((err) => setClientsError(err.message.replace('Firebase: ', '')));
  }, []);

  const updateItem = (index, field, value) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  // Which items have their feature list open. Collapsed by default so a quote
  // with several priced lines doesn't turn into a wall of inputs.
  const toggleFeatures = (index) => {
    setOpenFeatures((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const removeItem = (index) => {
    setLineItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const isComplete = (item) => !!item?.description?.trim() && item?.amount !== '' && item?.amount != null;
  const isBlank = (item) => !item?.description?.trim() && (item?.amount === '' || item?.amount == null);

  const filledItems = lineItems.filter(isComplete);
  // A row with only one half filled in would be dropped from the quote without
  // anyone noticing, so it's caught on submit rather than silently discarded.
  const partialItems = lineItems.filter((item) => !isComplete(item) && !isBlank(item));
  // No point stacking blank rows: the last one has to be finished first.
  const canAddItem = isComplete(lineItems[lineItems.length - 1]);
  const total = lineItemsTotal(filledItems);
  const discounted = applyPercent(total, discountPercent);
  const schedulePercent = schedule.reduce((sum, stage) => sum + (Number(stage.percent) || 0), 0);
  // Stages split the discounted figure, which is what the client actually owes.
  const stageAmounts = splitTotal(discounted, schedule);
  const careList = carePriceFor(category, careTier);
  const careDiscounted = careTier !== 'none' && careAmount !== '' && Number(careAmount) < careList;

  // Per-field, matching ContactScreen: the message sits under the offending
  // field rather than in one lump at the bottom of the form.
  const validate = () => {
    const errs = {};
    if (!clientEmail) errs.clientEmail = 'Choose which client this is for.';
    if (!title.trim()) errs.title = 'Give the quote a title.';
    if (!filledItems.length) errs.lineItems = 'Add at least one item with a description and a price.';
    else if (partialItems.length) errs.lineItems = 'One of your items is missing a description or a price — finish it or remove it.';
    // Stages that don't total 100% would bill the client the wrong amount.
    if (schedulePercent !== 100) errs.schedule = `Payment stages add up to ${schedulePercent}% — they need to total 100%.`;
    if (careTier !== 'none' && !careAmount) errs.carePlan = 'Give the care plan a monthly price.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await createQuote({
        clientEmail,
        title,
        category,
        lineItems: filledItems,
        notes,
        contractUrl,
        contractText,
        schedule,
        discountPercent,
        carePlan: careTier === 'none' ? null : {
          tier: careTier,
          monthlyAmount: careAmount,
          listAmount: careList,
          discountMonths: careDiscounted ? careDiscountMonths : null,
        },
        validityDays,
        clientName: clients.find((c) => c.email === clientEmail)?.name || null,
      }, user.email);
      setResult({ ok: true, message: `Quote sent to ${clientEmail}. It's on their account page now.` });
      setClientEmail('');
      setTitle('');
      setLineItems([emptyItem()]);
      setNotes('');
      setContractUrl('');
      setContractText('');
      setCategory('website');
      setSchedule(scheduleFor('website'));
      setCareTier('none');
      setCareAmount('');
      setValidityDays(String(QUOTE_VALIDITY_DAYS));
      setDiscountPercent('');
      setCareDiscountMonths('');
      clearDraft();
    } catch (err) {
      setResult({ ok: false, message: err.message.replace('Firebase: ', '') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>Quotes</div>
      <p style={styles.description}>Build a quote and send it straight to a client's account page, where they can accept or decline it.</p>

      <form onSubmit={handleSubmit}>
        <div style={styles.sectionLabel}>Client</div>
        <select
          style={{ ...styles.select, ...(errors.clientEmail ? styles.inputError : {}) }}
          value={clientEmail}
          onChange={(e) => { setClientEmail(e.target.value); clearError('clientEmail'); }}
        >
          <option value="">Select a client…</option>
          {clients.map((client) => (
            <option key={client.id} value={client.email}>{clientLabel(client)}</option>
          ))}
        </select>
        {errors.clientEmail && <p style={styles.fieldError}>{errors.clientEmail}</p>}
        {clientsError && <div style={styles.error}>Couldn't load clients: {clientsError}</div>}
        {!clientsError && clients.length === 0 && (
          <p style={styles.listMeta}>No clients yet — create one below and they'll appear here.</p>
        )}

        <div style={styles.sectionLabel}>Type of work</div>
        <div style={styles.categoryRow}>
          {['website', 'app'].map((id) => (
            <button
              key={id}
              type="button"
              style={{ ...styles.categoryButton, ...(category === id ? styles.categoryButtonActive : {}) }}
              onClick={() => changeCategory(id)}
            >
              {id === 'app' ? 'App' : 'Website'}
            </button>
          ))}
        </div>
        <p style={styles.hint}>
          Sets the payment stages and care plan prices to our published terms for that type. Both stay editable below.
        </p>

        <div style={styles.sectionLabel}>Title</div>
        <input
          style={{ ...styles.input, ...(errors.title ? styles.inputError : {}) }}
          type="text"
          placeholder="e.g. Website build — Acme Ltd"
          value={title}
          onChange={(e) => { setTitle(e.target.value); clearError('title'); }}
        />
        {errors.title && <p style={styles.fieldError}>{errors.title}</p>}

        <div style={styles.sectionLabel}>Price breakdown</div>
        {lineItems.map((item, i) => (
          <div key={i} style={styles.itemBlock}>
          <div style={styles.itemRow}>
            <input
              style={{ ...styles.itemDescription, ...(errors.lineItems ? styles.inputError : {}) }}
              type="text"
              placeholder="e.g. Design & wireframes"
              value={item.description}
              onChange={(e) => { updateItem(i, 'description', e.target.value); clearError('lineItems'); }}
            />
            <div style={styles.priceInputWrap}>
              <span style={styles.priceSymbol}>£</span>
              <input
                style={{ ...styles.priceInput, ...(errors.lineItems ? styles.inputError : {}) }}
                type="number"
                min="0"
                step="0.01"
                placeholder="800"
                value={item.amount}
                onChange={(e) => { updateItem(i, 'amount', e.target.value); clearError('lineItems'); }}
              />
            </div>
            <button
              type="button"
              style={styles.removeButton}
              onClick={() => removeItem(i)}
              disabled={lineItems.length === 1}
              aria-label="Remove item"
            >
              &times;
            </button>
          </div>

          {/* What this line covers. Priced at the parent, so the bullets have
              no amounts of their own. */}
          <div style={styles.featuresWrap}>
            <button
              type="button"
              style={{ ...styles.featuresToggle, ...(openFeatures[i] ? styles.featuresToggleOpen : {}) }}
              onClick={() => toggleFeatures(i)}
            >
              <span style={styles.featuresLabel}>What's included</span>
              <span style={styles.featurePreview}>
                {descriptionBullets(item.features).join(' · ') || 'Nothing listed'}
              </span>
              <span style={styles.featuresChevron}>{openFeatures[i] ? '▲' : '▼'}</span>
            </button>
            {openFeatures[i] && (
              <div style={styles.featuresBody}>
                <DescriptionItemsInput
                  value={item.features || ''}
                  onChange={(value) => updateItem(i, 'features', value)}
                  placeholder="e.g. Contact form"
                />
              </div>
            )}
          </div>
          </div>
        ))}
        <button
          type="button"
          style={{ ...styles.addButton, ...(canAddItem ? {} : styles.disabled) }}
          onClick={() => setLineItems((prev) => [...prev, emptyItem()])}
          disabled={!canAddItem}
          title={canAddItem ? '' : 'Finish the item above first'}
        >
          + Add item
        </button>
        {errors.lineItems && <p style={styles.fieldError}>{errors.lineItems}</p>}

        <div style={styles.sectionLabel}>Discount (optional)</div>
        <div style={styles.validityRow}>
          <input
            style={styles.validityInput}
            type="number"
            min="0"
            max="100"
            placeholder="0"
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)}
          />
          <span style={styles.percentSymbol}>% off</span>
        </div>
        <p style={styles.hint}>
          Items stay at full price on the quote; the client sees the original struck through and what they're saving.
        </p>

        <div style={styles.sectionLabel}>Payment stages</div>
        {schedule.map((stage, i) => (
          <div key={i} style={styles.itemRow}>
            <input
              style={styles.itemDescription}
              type="text"
              value={stage.label}
              onChange={(e) => setSchedule((prev) => prev.map((s, si) => (
                si === i ? { ...s, label: e.target.value } : s
              )))}
            />
            <div style={styles.percentWrap}>
              <input
                style={{ ...styles.percentInput, ...(errors.schedule ? styles.inputError : {}) }}
                type="number"
                min="0"
                max="100"
                value={stage.percent}
                onChange={(e) => { changeStagePercent(i, e.target.value); clearError('schedule'); }}
              />
              <span style={styles.percentSymbol}>%</span>
            </div>
            <span style={styles.stageAmount}>{formatMoney(stageAmounts[i]?.amount)}</span>
          </div>
        ))}
        {errors.schedule && <p style={styles.fieldError}>{errors.schedule}</p>}

        <div style={styles.sectionLabel}>Valid for</div>
        <div style={styles.validityRow}>
          <input
            style={styles.validityInput}
            type="number"
            min="1"
            max="365"
            value={validityDays}
            onChange={(e) => setValidityDays(e.target.value)}
          />
          <span style={styles.percentSymbol}>days</span>
        </div>
        <p style={styles.hint}>
          After this the client can't accept it — the rules block it, not just the page. Shown on their quote and the PDF.
        </p>

        <div style={styles.sectionLabel}>Care plan</div>
        <select
          style={styles.select}
          value={careTier}
          onChange={(e) => { changeCareTier(e.target.value); clearError('carePlan'); }}
        >
          <option value="none">No care plan</option>
          <option value="essential">{CARE_TIER_LABELS.essential}</option>
          <option value="active">{CARE_TIER_LABELS.active}</option>
        </select>
        {careTier !== 'none' && (
          <>
            <div style={styles.carePriceWrap}>
              <span style={styles.priceSymbol}>£</span>
              <input
                style={{ ...styles.priceInput, ...(errors.carePlan ? styles.inputError : {}) }}
                type="number"
                min="0"
                step="0.01"
                value={careAmount}
                onChange={(e) => { setCareAmount(e.target.value); clearError('carePlan'); }}
              />
            </div>
            <p style={styles.hint}>
              {careDiscounted ? (
                <>
                  Below our {formatMonthly(careList)} rate — the client will see{' '}
                  <span style={styles.strike}>{formatMonthly(careList)}</span>
                  {formatMonthly(careAmount)} on their quote.
                  {Number(careDiscountMonths) > 0
                    ? ` Reverts to ${formatMonthly(careList)} after ${careDiscountMonths} month${Number(careDiscountMonths) === 1 ? '' : 's'}.`
                    : ' Set a duration below, or leave blank to keep this rate indefinitely.'}
                </>
              ) : (
                <>
                  Our price for {category === 'app' ? 'an app' : 'a website'} is{' '}
                  {formatMonthly(careList)} — lower it for a special offer and the client sees the saving.
                </>
              )}
            </p>
            {careDiscounted && (
              <>
                <div style={styles.sectionLabel}>Discounted rate lasts</div>
                <div style={styles.validityRow}>
                  <input
                    style={styles.validityInput}
                    type="number"
                    min="1"
                    max="60"
                    placeholder="—"
                    value={careDiscountMonths}
                    onChange={(e) => setCareDiscountMonths(e.target.value)}
                  />
                  <span style={styles.percentSymbol}>months</span>
                </div>
                <p style={styles.hint}>
                  After this many invoices the care plan bills at {formatMonthly(careList)} automatically.
                  Leave blank for no end date.
                </p>
              </>
            )}
          </>
        )}
        {errors.carePlan && <p style={styles.fieldError}>{errors.carePlan}</p>}

        <div style={styles.sectionLabel}>Contract wording (optional)</div>
        <textarea
          style={{ ...styles.textarea, minHeight: 140 }}
          placeholder="Paste the full contract text here."
          value={contractText}
          onChange={(e) => setContractText(e.target.value)}
        />
        <p style={styles.hint}>
          Stored on the quote itself, so it can't change after signing — this is what the client's signed PDF contains.
        </p>

        <div style={styles.sectionLabel}>Original document link (optional)</div>
        <input
          style={styles.input}
          type="url"
          placeholder="https://drive.google.com/…"
          value={contractUrl}
          onChange={(e) => setContractUrl(e.target.value)}
        />
        <p style={styles.hint}>
          Add either one and the client must type their name to sign before they can accept.
        </p>

        <div style={styles.sectionLabel}>Notes (optional)</div>
        <textarea
          style={styles.textarea}
          placeholder="Anything the client should know before deciding."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {Number(discountPercent) > 0 && (
          <>
            <div style={styles.subtotalRow}>
              <span>Subtotal</span>
              <span>{formatMoney(total)}</span>
            </div>
            <div style={{ ...styles.subtotalRow, ...styles.discountRow }}>
              <span>Discount {Number(discountPercent)}%</span>
              <span>−{formatMoney(total - discounted)}</span>
            </div>
          </>
        )}

        <div style={styles.totalRow}>
          <span>Total</span>
          <span style={styles.totalValue}>{formatMoney(discounted)}</span>
        </div>

        <button type="submit" style={styles.submit} disabled={submitting}>
          {submitting ? 'Sending…' : 'Send quote'}
        </button>
      </form>

      {result && <div style={result.ok ? styles.success : styles.error}>{result.message}</div>}
    </div>
  );
}
